import React, { useState, useEffect, useCallback, useContext } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { AuthContext } from '../contexts/AuthContext';
import EditNameModal from './EditNameModal';
import ChangeEmailModal from './ChangeEmailModal';
import {
  BORDER_RADIUS,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
} from '../constants/layout';
import {
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

export default function ContactInfoModal({ onClose }) {
  const { user } = useContext(AuthContext);
  const functions = getFunctions(getApp(), 'us-central1');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [loadingContact, setLoadingContact] = useState(true);

  const [showChangeName, setShowChangeName] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  // Phone editing
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  const fetchContactInfo = async () => {
    try {
      const getUserContactInfo = httpsCallable(functions, 'getUserContactInfo');
      const result = await getUserContactInfo({ platform: 'web' });
      const data = result.data;
      if (data) {
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setContactEmail(data.contactEmail || user?.email || '');
        setContactPhone(data.contactPhone || '');
      }
    } catch (err) {
      console.error('Error fetching contact info:', err);
    } finally {
      setLoadingContact(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => setIsVisible(true));
    fetchContactInfo();
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  const handleStartPhoneEdit = () => {
    setPhoneInput(contactPhone);
    setEditingPhone(true);
  };

  const handleSavePhone = async () => {
    if (phoneInput === contactPhone) {
      setEditingPhone(false);
      return;
    }
    setPhoneSaving(true);
    try {
      const updateUserContactPhone = httpsCallable(functions, 'updateUserContactPhone');
      await updateUserContactPhone({ phone: phoneInput });
      setContactPhone(phoneInput);
      setEditingPhone(false);
    } catch (err) {
      console.error('Failed to save phone:', err);
    } finally {
      setPhoneSaving(false);
    }
  };

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || '—';

  return (
    <div
      className={`cim-overlay ${isVisible ? 'cim-visible' : ''}`}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="cim-modal">
        <div className="cim-header">
          <h3>Contact Info</h3>
          <button className="cim-close" onClick={handleClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="cim-body">
          {loadingContact ? (
            <div className="cim-loading">Loading contact info...</div>
          ) : (
            <div className="cim-rows">
              <button className="cim-row" onClick={() => setShowChangeName(true)}>
                <span className="cim-label">Name</span>
                <span className="cim-value">{displayName}</span>
              </button>
              <button className="cim-row" onClick={() => setShowChangeEmail(true)}>
                <span className="cim-label">Email</span>
                <span className="cim-value">{contactEmail || '—'}</span>
              </button>
              {editingPhone ? (
                <div className="cim-row cim-row-editing">
                  <span className="cim-label">Phone</span>
                  <div className="cim-phone-edit">
                    <input
                      type="tel"
                      value={phoneInput}
                      onChange={e => setPhoneInput(e.target.value)}
                      className="cim-input"
                      placeholder="Phone number"
                      autoFocus
                      disabled={phoneSaving}
                    />
                    <button
                      onClick={handleSavePhone}
                      className="cim-save-inline"
                      disabled={phoneSaving || !phoneInput.trim()}
                    >
                      {phoneSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="cim-row" onClick={handleStartPhoneEdit}>
                  <span className="cim-label">Phone</span>
                  <span className="cim-value">
                    {contactPhone || 'Add phone number'}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <EditNameModal
        isOpen={showChangeName}
        onClose={() => setShowChangeName(false)}
        initialFirstName={firstName}
        initialLastName={lastName}
        onSaved={(f, l) => { setFirstName(f); setLastName(l); }}
      />

      <ChangeEmailModal
        isOpen={showChangeEmail}
        onClose={() => setShowChangeEmail(false)}
        currentEmail={contactEmail}
        onSaved={(e) => setContactEmail(e)}
      />

      <style jsx>{`
        .cim-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0,0,0,0.5);
          display: flex; align-items: flex-end; justify-content: center;
          z-index: 1000;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .cim-overlay.cim-visible { opacity: 1; }

        .cim-modal {
          background-color: var(--color-background, #fff);
          border-radius: 16px 16px 0 0;
          width: 100%; max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          transform: translateY(100%);
          transition: transform 0.25s ease;
        }
        .cim-overlay.cim-visible .cim-modal {
          transform: translateY(0);
        }

        .cim-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
          border-bottom: 1px solid var(--color-border);
        }
        .cim-header h3 {
          font-size: ${FONT_TITLE3}; font-weight: 600; margin: 0;
          color: var(--color-text-primary);
        }
        .cim-close {
          background: none; border: none; cursor: pointer;
          padding: 4px; color: var(--color-text-secondary);
        }
        .cim-close:hover { color: var(--color-text-primary); }

        .cim-body { padding: ${SPACING_BUTTON} 0; }

        .cim-loading {
          text-align: center; padding: ${SPACING_CARD_BODY};
          color: var(--color-text-secondary); font-size: ${FONT_BODY};
        }

        .cim-rows { display: flex; flex-direction: column; }

        .cim-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          background: none; border: none; cursor: pointer;
          text-align: left; width: 100%;
          transition: background-color 0.15s ease;
        }
        .cim-row:hover { background-color: var(--color-surface-muted, rgba(0,0,0,0.03)); }
        .cim-row-editing { cursor: default; }
        .cim-row-editing:hover { background-color: transparent; }

        .cim-label {
          font-size: ${FONT_BODY}; color: var(--color-text-primary); font-weight: 500;
        }
        .cim-value {
          font-size: ${FONT_BODY}; color: var(--color-text-secondary);
          text-align: right; flex-shrink: 1; min-width: 0;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          max-width: 60%;
        }

        .cim-phone-edit {
          display: flex; gap: ${SPACING_ELEMENT}; align-items: center;
        }

        .cim-input {
          padding: ${SPACING_COMPACT};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          background-color: var(--color-surface, #fff);
          box-sizing: border-box;
          width: 100%;
        }
        .cim-input:focus { outline: none; border-color: var(--color-accent); }
        .cim-input:disabled { opacity: 0.6; }

        .cim-save-inline {
          padding: ${SPACING_COMPACT} ${SPACING_COMPONENT};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none; border-radius: ${BORDER_RADIUS};
          font-size: ${FONT_FOOTNOTE}; font-weight: 600;
          cursor: pointer; white-space: nowrap;
        }
        .cim-save-inline:disabled { opacity: 0.5; cursor: not-allowed; }

        @media (min-width: 601px) {
          .cim-overlay { align-items: center; }
          .cim-modal { border-radius: ${BORDER_RADIUS}; }
        }
      `}</style>
    </div>
  );
}
